import React from 'react';
import { Space, Card, Typography, Alert } from 'antd';
import { getAddOnConfig } from '@shared/config/subscription-addons';
import type { AddOnType } from '@shared/config/subscription-addons';
import { withDevModalFilePath } from '../common';

const { Text } = Typography;

export interface AddonPreview {
  addonType: AddOnType;
  quantity: number;
  unitPrice: number;
  unitPriceWithTax: number;
  proratedAmount: number;
  proratedAmountWithTax: number;
  proratedTax: number;
  proratedDays: number;
  totalDays: number;
  periodStart: Date | string;
  periodEnd: Date | string;
  fullMonthAmount: number;
  fullMonthAmountWithTax: number;
  fullMonthTax: number;
  nextBillingDate: Date | string;
  totalInitialCharge: number;
  totalInitialChargeWithTax: number;
  totalInitialTax: number;
  // Stripe APIから取得したかどうかのフラグ
  isStripePreview?: boolean;
}

interface AddonAddConfirmDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  preview: AddonPreview | null;
  loading?: boolean;
}

const { Modal } = withDevModalFilePath('client/src/components/company/AddonAddConfirmDialog.tsx');

/**
 * 追加オプション追加確認ダイアログ
 */
export const AddonAddConfirmDialog: React.FC<AddonAddConfirmDialogProps> = ({
  open,
  onConfirm,
  onCancel,
  preview,
  loading = false,
}) => {
  if (!preview) {
    return null;
  }

  const config = getAddOnConfig(preview.addonType);
  const formatDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  return (
    <Modal
      title="追加オプション追加の確認"
      open={open}
      onOk={onConfirm}
      onCancel={onCancel}
      okText="確認して追加"
      cancelText="キャンセル"
      width={600}
      confirmLoading={loading}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* 追加内容 */}
        <div>
          <Text strong style={{ fontSize: 16 }}>追加内容:</Text>
          <div style={{ marginTop: 8, fontSize: 16 }}>
            {config.label} <strong>{preview.quantity}{config.unitLabel}</strong>
          </div>
        </div>

        {/* 料金詳細 */}
        <div>
          <Text strong style={{ fontSize: 16 }}>料金詳細:</Text>

          {/* 初月（日割り） */}
          <Card
            size="small"
            title="初月（日割り計算）"
            style={{ marginTop: 12 }}
            styles={{ header: { backgroundColor: '#f0f5ff' } }}
          >
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <div>
                <Text type="secondary">請求期間:</Text>{' '}
                <Text>{formatDate(preview.periodStart)} 〜 {formatDate(preview.periodEnd)}</Text>
              </div>
              <div style={{ marginTop: 8, fontSize: 16 }}>
                <Text strong style={{ fontSize: 18 }}>
                  ¥{preview.proratedAmount.toLocaleString()}{' '}
                </Text>
                <Text type="secondary" style={{ color: '#999' }}>
                  (税込{preview.proratedAmountWithTax.toLocaleString()}円)
                </Text>
              </div>
              {!preview.isStripePreview && (
                <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                  ¥{preview.fullMonthAmount.toLocaleString()} × {preview.proratedDays}/{preview.totalDays}日
                </div>
              )}
            </Space>
          </Card>

          {/* 次月以降 */}
          <Card
            size="small"
            title="2月以降（満額）"
            style={{ marginTop: 12 }}
            styles={{ header: { backgroundColor: '#f6ffed' } }}
          >
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <div>
                <Text type="secondary">月額料金:</Text>{' '}
                <Text strong style={{ fontSize: 16 }}>
                  ¥{preview.fullMonthAmount.toLocaleString()}/月{' '}
                </Text>
                <Text type="secondary" style={{ color: '#999' }}>
                  (税込{preview.fullMonthAmountWithTax.toLocaleString()}円/月)
                </Text>
              </div>
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">次回請求日:</Text>{' '}
                <Text>{formatDate(preview.nextBillingDate)}</Text>
              </div>
            </Space>
          </Card>
        </div>

        {/* 合計・注意書き */}
        <Alert
          type="warning"
          message={
            <div>
              <div style={{ fontSize: 20, marginBottom: 8 }}>
                <Text strong>
                  初回請求額: ¥{preview.totalInitialCharge.toLocaleString()}{' '}
                </Text>
                <Text type="secondary" style={{ color: '#999', fontSize: 16 }}>
                  (税込{preview.totalInitialChargeWithTax.toLocaleString()}円)
                </Text>
              </div>
              <Text type="secondary" style={{ fontSize: 13 }}>
                ⚠️ この料金は即座に請求されます
              </Text>
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12, color: '#999' }}>
                  ※ 追加オプションをキャンセルされた場合、日割りでの返金はございません
                </Text>
              </div>
            </div>
          }
          showIcon
        />

        <div style={{ fontSize: 12, color: '#999', marginTop: -8 }}>
          {preview.isStripePreview ? (
            <>※ 日割りは秒単位で計算されるため、契約時刻により数円程度異なる場合があります</>
          ) : (
            <>※ 料金は目安です。実際の請求額は請求書でご確認ください。</>
          )}
        </div>
      </Space>
    </Modal>
  );
};

export default AddonAddConfirmDialog;
